/**
 * Firestore readers for the Exposed Sensitive Documents page.
 *
 * Two collections, both written by the `publish-exposure` step and both read-only here:
 *
 *  - `organisations/{org}/exposed_sensitive_documents` - one row per exposed sensitive
 *    document, loaded in full (paged `getDocs`, never `onSnapshot`) once per org selection.
 *    The count query and the first page are issued together, and the store emits after every
 *    page, so the page renders from the first batch and fills in as the rest arrive. Every
 *    filter and sort the page offers runs client-side over the rows loaded so far.
 *  - `organisations/{org}/exposed_sensitive_documents_metadata` - one document per provider,
 *    read with a single `getDocs`, cached per org selection. It also answers the sidebar's
 *    "does this org have the feature at all" question.
 *
 * Neither collection is read unless the signed-in user holds `org:security_findings` on the
 * selected org: the claim is checked client-side first, so a reader without it issues zero
 * Firestore reads. A `permission-denied` answer (an org whose infra rules are not deployed yet)
 * is treated exactly as "no data", never surfaced.
 *
 * A third collection, `organisations/{org}/exposed_sensitive_documents_status`, holds the
 * reviewer status records (design spec 9.3). It is read the same way - one `getDocs` per org
 * selection, never `onSnapshot` - but concurrently with the rows and on its own loading flag, and
 * any error, `permission-denied` included, is surfaced as a `failed` read: a status the page could
 * not read must never be shown as `open`.
 */
import {
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { derived, readable, writable, type Readable } from "svelte/store";

import {
  hasActiveMetadata,
  parseExposedDocumentRow,
  parseExposureMetadata,
  type ExposedDocumentsProviderMetadata,
  type ExposedSensitiveDocument,
  type Provider,
} from "$lib/components/security/exposedSensitiveDocuments";
import {
  joinStatusRecords,
  parseStatusRecord,
  type ExposedDocumentWithStatus,
  type StatusLoadState,
  type StatusRecord,
} from "$lib/components/security/exposedSensitiveDocuments/status";
import { chainStores } from "$lib/utils/firestore";
import { hasOrgClaim } from "$lib/utils/orgClaims";
import { distinctBy } from "$lib/utils/stores";
import { firebaseApp } from "$lib/utils/env";
import { authStateStore } from "./user";
import { selectedOrgIdStore, selectedOrgStore } from "./orgs";

export const EXPOSED_SENSITIVE_DOCUMENTS_CLAIM = "org:security_findings";
export const EXPOSED_SENSITIVE_DOCUMENTS_COLLECTION = "exposed_sensitive_documents";
export const EXPOSED_SENSITIVE_DOCUMENTS_METADATA_COLLECTION = "exposed_sensitive_documents_metadata";
export const EXPOSED_SENSITIVE_DOCUMENTS_STATUS_COLLECTION = "exposed_sensitive_documents_status";
/** Rows per `getDocs` page. Matches the findings store's batch size. */
export const EXPOSED_DOCUMENTS_BATCH_SIZE = 500;

const isPermissionDenied = (error: unknown): boolean =>
  typeof error === "object" && error !== null && (error as { code?: string }).code === "permission-denied";

// ---------------------------------------------------------------------------------------------
// The query target: which org to read, and whether the user may read it at all.
//
// This is where the per-org-selection caching lives. `selectedOrgStore` is an `onSnapshot`
// document store, so it re-emits whenever anything on the org document changes, and
// `authStateStore` re-emits on every auth refresh; a plain `derived` would hand `chainStores` a
// fresh value each time and `chainStores` would tear down and recreate the reader below, i.e.
// re-query. Emitting only when the (orgId, allowed) pair actually changes means each reader is
// created once per org selection: navigating between pages, re-sorting and re-filtering do not
// re-query, and selecting a different org does.
//
// The dedupe is `distinctBy`, whose memo is per subscription rather than per module. That
// matters when the last subscriber drops - leaving the page, signing out of the sidebar - and a
// later one arrives on the same (orgId, allowed) pair: a module-level memo would recognise the
// key, skip the emission, and leave the new subscriber's reader never created and its store
// stuck on the empty default.
// ---------------------------------------------------------------------------------------------

interface ExposureQueryTarget {
  orgId: string;
  /** Whether the signed-in user holds the claim on this org, decided client-side. */
  allowed: boolean;
}

const exposureQueryTargetStore: Readable<ExposureQueryTarget | undefined> = distinctBy(
  derived([selectedOrgIdStore, selectedOrgStore, authStateStore], ([selectedOrgId, selectedOrg, authState]) =>
    selectedOrgId
      ? {
          orgId: selectedOrgId,
          allowed: hasOrgClaim(selectedOrg, authState.userInfo?.email, EXPOSED_SENSITIVE_DOCUMENTS_CLAIM),
        }
      : undefined,
  ),
  (target) => (target ? `${target.orgId}:${target.allowed}` : ""),
);

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

export interface ExposedSensitiveDocumentsState {
  /** The rows loaded so far, in document-id order; grows by one page per emission. */
  rows: ExposedSensitiveDocument[];
  /** True until the last page has landed. */
  isLoading: boolean;
  /**
   * Server-side count of the collection, so the page can show progress before the last page.
   * `null` until the count query answers: it runs alongside the first page, which may land first.
   * Stays `null` if the count query fails; the rows load regardless.
   */
  totalCount: number | null;
  /** `rows.length`. */
  loadedCount: number;
  error?: string;
}

const NO_EXPOSED_DOCUMENTS: ExposedSensitiveDocumentsState = {
  rows: [],
  isLoading: false,
  totalCount: 0,
  loadedCount: 0,
};

/**
 * Read every exposed sensitive document for an org, one 500-row page at a time.
 *
 * `provider` narrows the read with an equality `where`; the page leaves it unset and filters
 * client-side over the loaded rows, but a future caller that only ever wants one provider
 * can push the filter to Firestore without another reader. The explicit `orderBy(documentId())`
 * is what makes `startAfter` a stable cursor across pages.
 *
 * The count query is issued alongside the first page rather than before it, and whichever
 * answers first emits: `totalCount` stays `null` until the count lands. Pages follow each other
 * with no pause, and each one emits. The page count, not the server count, ends the load, so a
 * count that is stale by the time the last page lands cannot stall or truncate it. A failed count
 * is logged and leaves `totalCount` at `null`: the pages keep loading and the page falls back to
 * its no-count progress copy. A failed page fails the read.
 */
const createExposedSensitiveDocumentsStore = (orgId: string, provider?: Provider) =>
  readable<ExposedSensitiveDocumentsState>({ ...NO_EXPOSED_DOCUMENTS, isLoading: true, totalCount: null }, (set) => {
    // Set on unsubscribe, and on a failed page so a late count answer cannot overwrite the error.
    let cancelled = false;

    const load = async () => {
      try {
        const firestore = getFirestore(await firebaseApp);
        const collectionRef = collection(firestore, `organisations/${orgId}/${EXPOSED_SENSITIVE_DOCUMENTS_COLLECTION}`);
        const filters = provider ? [where("provider", "==", provider)] : [];

        const rows: ExposedSensitiveDocument[] = [];
        let totalCount: number | null = null;
        let hasMore = true;
        const emit = () => set({ rows: [...rows], isLoading: hasMore, totalCount, loadedCount: rows.length });

        // Not awaited: the first page goes out in the same tick. A failed count is only progress
        // lost, so it leaves `totalCount` at `null` rather than failing the rows already shown.
        getCountFromServer(query(collectionRef, ...filters)).then(
          (snapshot) => {
            if (cancelled) return;
            totalCount = snapshot.data().count;
            emit();
          },
          (error: unknown) => {
            // `permission-denied` fails the first page too, and that answer is the one kept.
            if (cancelled || isPermissionDenied(error)) return;
            console.error("[orgExposedSensitiveDocuments] Error counting exposed sensitive documents:", error);
          },
        );

        let lastDocument: QueryDocumentSnapshot | undefined = undefined;
        while (hasMore && !cancelled) {
          const constraints: QueryConstraint[] = [
            ...filters,
            orderBy(documentId()),
            limit(EXPOSED_DOCUMENTS_BATCH_SIZE),
          ];
          if (lastDocument) constraints.push(startAfter(lastDocument));

          const snapshot = await getDocs(query(collectionRef, ...constraints));
          if (cancelled) return;

          rows.push(...snapshot.docs.map((document) => parseExposedDocumentRow(document.id, document.data())));
          lastDocument = snapshot.docs[snapshot.docs.length - 1];
          hasMore = snapshot.docs.length === EXPOSED_DOCUMENTS_BATCH_SIZE;

          emit();
        }
      } catch (error) {
        if (cancelled) return;
        cancelled = true;
        if (isPermissionDenied(error)) {
          // The org's Firestore rules are not deployed yet. Same answer as an empty collection.
          set(NO_EXPOSED_DOCUMENTS);
          return;
        }
        console.error("[orgExposedSensitiveDocuments] Error loading exposed sensitive documents:", error);
        set({
          ...NO_EXPOSED_DOCUMENTS,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  });

/** Every exposed sensitive document for the selected org, loaded once per org selection. */
export const orgExposedSensitiveDocumentsStore: Readable<ExposedSensitiveDocumentsState> = chainStores(
  exposureQueryTargetStore,
  NO_EXPOSED_DOCUMENTS,
  (target) => (target.allowed ? createExposedSensitiveDocumentsStore(target.orgId) : readable(NO_EXPOSED_DOCUMENTS)),
);

// ---------------------------------------------------------------------------------------------
// Per-provider metadata
// ---------------------------------------------------------------------------------------------

export interface ExposureMetadataState {
  metadata: ExposedDocumentsProviderMetadata[];
  isLoading: boolean;
  error?: string;
}

const NO_EXPOSURE_METADATA: ExposureMetadataState = { metadata: [], isLoading: false };

const createExposureMetadataStore = (orgId: string) =>
  readable<ExposureMetadataState>({ metadata: [], isLoading: true }, (set) => {
    let cancelled = false;

    const load = async () => {
      try {
        const firestore = getFirestore(await firebaseApp);
        const snapshot = await getDocs(
          query(
            collection(firestore, `organisations/${orgId}/${EXPOSED_SENSITIVE_DOCUMENTS_METADATA_COLLECTION}`),
            orderBy(documentId()),
          ),
        );
        if (cancelled) return;
        set({
          // A document whose status this build cannot read is dropped, not guessed at: the page
          // would otherwise paint a skipped-provider warning and dim live rows on a string it
          // does not understand.
          metadata: snapshot.docs.flatMap((document) => {
            const meta = parseExposureMetadata(document.id, document.data());
            return meta ? [meta] : [];
          }),
          isLoading: false,
        });
      } catch (error) {
        if (cancelled) return;
        if (isPermissionDenied(error)) {
          // Expected for an environment whose infra Firestore rules are not deployed yet: the
          // sidebar entry and the page both read it as "this org has no metadata". Not logged.
          set(NO_EXPOSURE_METADATA);
          return;
        }
        console.error("[orgExposedSensitiveDocuments] Error loading exposure metadata:", error);
        set({ ...NO_EXPOSURE_METADATA, error: error instanceof Error ? error.message : "Unknown error" });
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  });

/**
 * The per-provider metadata documents for the selected org. One `getDocs` per org selection;
 * without the claim it resolves to "no metadata" without touching Firestore.
 */
export const orgExposedSensitiveDocumentsMetadataStore: Readable<ExposureMetadataState> = chainStores(
  exposureQueryTargetStore,
  NO_EXPOSURE_METADATA,
  (target) => (target.allowed ? createExposureMetadataStore(target.orgId) : readable(NO_EXPOSURE_METADATA)),
);

/**
 * The sidebar gate (spec 6.1): the entry is shown only when at least one provider reads
 * `published` or `skipped`. An org whose metadata is all `retired` has had its rows swept, so it
 * is back to having no page worth linking to and the page itself shows the off state.
 */
export const exposedSensitiveDocumentsAvailable: Readable<boolean> = derived(
  orgExposedSensitiveDocumentsMetadataStore,
  (state) => hasActiveMetadata(state.metadata),
);

// ---------------------------------------------------------------------------------------------
// Reviewer status
//
// Its own reader on the same query target, so the single status `getDocs` is issued alongside
// the rows' count and first page rather than after the last page, and its `state` is never part
// of the page spinner (I14): rows render on arrival and read `open` until the status read lands.
// ---------------------------------------------------------------------------------------------

export interface ExposureStatusState {
  state: StatusLoadState;
  /** Row id -> record. Only non-`open` rows have one. */
  records: ReadonlyMap<string, StatusRecord>;
}

/** No org, or no claim: nothing to read, so nothing is pending and nothing has failed. */
const NO_EXPOSURE_STATUS: ExposureStatusState = { state: "ready", records: new Map() };

/**
 * The reader for the current org selection, so `setStatus` patches the map the page is showing.
 * Keyed by org: a save that lands after an org switch carries the old org and is dropped.
 */
let activeStatusPatch:
  | { orgId: string; patch: (id: string, record: StatusRecord | undefined) => void }
  | undefined;

const createExposureStatusStore = (orgId: string): Readable<ExposureStatusState> => {
  // A patch that lands before the read resolves is kept and re-applied over the read's result.
  const patches = new Map<string, StatusRecord | undefined>();
  const store = writable<ExposureStatusState>({ state: "loading", records: new Map() }, (set) => {
    let cancelled = false;

    const load = async () => {
      try {
        const firestore = getFirestore(await firebaseApp);
        const snapshot = await getDocs(
          collection(firestore, `organisations/${orgId}/${EXPOSED_SENSITIVE_DOCUMENTS_STATUS_COLLECTION}`),
        );
        if (cancelled) return;
        const records = new Map<string, StatusRecord>();
        for (const document of snapshot.docs) {
          const record = parseStatusRecord(document.data());
          if (record) records.set(document.id, record);
        }
        for (const [id, record] of patches) {
          if (record) records.set(id, record);
          else records.delete(id);
        }
        set({ state: "ready", records });
      } catch (error) {
        if (cancelled) return;
        // Unlike rows and metadata, `permission-denied` is not "no data" here: the page shows
        // the failed state rather than a column of `open` it cannot vouch for.
        console.error("[orgExposedSensitiveDocuments] Error loading exposed sensitive document status:", error);
        set({ state: "failed", records: new Map() });
      }
    };

    const patch = (id: string, record: StatusRecord | undefined) => {
      patches.set(id, record);
      store.update(({ state, records }) => {
        const next = new Map(records);
        if (record) next.set(id, record);
        else next.delete(id);
        return { state, records: next };
      });
    };
    const active = { orgId, patch };
    activeStatusPatch = active;

    load();

    return () => {
      cancelled = true;
      if (activeStatusPatch === active) activeStatusPatch = undefined;
    };
  });
  return { subscribe: store.subscribe };
};

/**
 * The reviewer status records for the selected org, read once per org selection, plus the
 * in-place patch the panel applies after a save: a record from the route's `200`, or `undefined`
 * after a `204` (set back to `open`). No reload, no re-read.
 */
export const orgExposedSensitiveDocumentsStatusStore: Readable<ExposureStatusState> & {
  setStatus: (orgId: string, id: string, record: StatusRecord | undefined) => void;
} = {
  subscribe: chainStores(exposureQueryTargetStore, NO_EXPOSURE_STATUS, (target) =>
    target.allowed ? createExposureStatusStore(target.orgId) : readable(NO_EXPOSURE_STATUS),
  ).subscribe,
  setStatus: (orgId, id, record) => {
    if (activeStatusPatch?.orgId === orgId) activeStatusPatch.patch(id, record);
  },
};

export interface ExposedSensitiveDocumentsWithStatusState extends Omit<ExposedSensitiveDocumentsState, "rows"> {
  rows: ExposedDocumentWithStatus[];
}

/** The rows with their status records joined on by id. `isLoading` is the rows' alone. */
export const orgExposedSensitiveDocumentsWithStatusStore: Readable<ExposedSensitiveDocumentsWithStatusState> = derived(
  [orgExposedSensitiveDocumentsStore, orgExposedSensitiveDocumentsStatusStore],
  ([documents, status]) => ({ ...documents, rows: joinStatusRecords(documents.rows, status.records) }),
);
